// Datenzugriff der ERP-Steuerung (ERP/CRM-Plan Abschnitt 8 — Phase 5).
//
// Unrein: hier liegen ausschliesslich Supabase-Aufrufe, das
// snake_case<->camelCase-Mapping und die Zusammenfuehrung mit Kundenstamm,
// Profilen und Freelancern. Die Fachlichkeit — Summen, Reichweite,
// Deckungsbeitrag, Ampeln, Wochenraster — steht pur und getestet in
// erpSteering.ts und wird von hier nur AUFGERUFEN, nicht nachgebaut.
//
// ─── Lesen ueber Aggregat-RPCs, schreiben direkt ────────────────────────────
// Die drei Auswertungen kommen aus SECURITY-DEFINER-RPCs
// (supabase/erp_steering.sql, MIGRATIONS.md Nr. 23) — der Browser bekommt
// keine Rechnungs-Rohdaten fuer das Cockpit, dieselbe Entscheidung wie bei den
// Karten-Aggregaten (Codex-Befund 5.3).
//
// Die beiden Pflege-Tabellen `project_order_values` und `effort_rates` werden
// dagegen DIREKT geschrieben. Bewusst anders als bei den Gutschriften: dort
// erzwingt eine Uebergangsmatrix den RPC-Weg, hier gibt es keinen Zustand, den
// eine RPC bewachen koennte — es sind zwei Zahlen, die ein Administrator
// setzt. Geschuetzt sind sie durch die interne RLS (`not is_freelancer()`).
//
// ─── Das Modul ist NICHT-FATAL ─────────────────────────────────────────────
// Fehlt Nr. 23, scheitern die RPC-Aufrufe mit PGRST202. Die Ladefunktionen
// werfen dann (die Ansicht zeigt den Fehler je Karte); der Auftragswert beim
// Annehmen eines Angebots wird dagegen BEST EFFORT geschrieben — eine fehlende
// Migration darf keine Projektanlage kippen.

import { supabase } from './supabaseClient';
import { describeError } from './errorMessage';
import { ladeAlleSeiten } from './seitenweise';
import { Customer, Report } from '../types';
import {
    AuftragsbestandRow,
    KapazitaetAssignment,
    KapazitaetFreelancer,
    KapazitaetKoordinator,
    KAPAZITAET_FREELANCER_DEFAULT,
    KAPAZITAET_KOORDINATOR_DEFAULT,
    KapazitaetProjekt,
    LiquiditaetsBeleg,
    NachkalkulationRow,
    plzAusText,
} from './erpSteering';
import {
    FreelancerQualification,
    FreelancerStatus,
    QualiTyp,
    einsatzbereitschaft,
} from './freelancerLogic';

// ═══════════════════════════════════════════════════════════════════════════
// Mandant
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Die company_id des angemeldeten Profils. Wortgleich mit
 * freelancerService.currentCompanyId — bewusst dupliziert statt importiert,
 * damit dieses Modul nicht am Freelancer-Modul haengt, das es fachlich nur
 * liest.
 */
export const currentCompanyId = async (): Promise<string> => {
    const { data: auth } = await supabase.auth.getUser();
    const userId = auth?.user?.id;
    if (!userId) throw new Error('Nicht angemeldet.');

    const { data, error } = await supabase
        .from('profiles')
        .select('company_id')
        .eq('id', userId)
        .maybeSingle();
    if (error) throw new Error(describeError(error));

    const companyId = (data as { company_id?: string } | null)?.company_id;
    if (!companyId) {
        throw new Error(
            'Das eigene Profil hat keine Firmenzuordnung (company_id). ' +
            'Bitte die Migration companies.sql (Nr. 9) ausführen bzw. das Profil zuordnen.',
        );
    }
    return String(companyId);
};

const zahl = (v: unknown): number => {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
};

// ═══════════════════════════════════════════════════════════════════════════
// 1) Auswertungen (RPCs)
// ═══════════════════════════════════════════════════════════════════════════

export const fetchAuftragsbestand = async (): Promise<AuftragsbestandRow[]> => {
    const { data, error } = await supabase.rpc('erp_auftragsbestand');
    if (error) throw new Error(describeError(error));
    return ((data as Record<string, unknown>[]) || []).map((row) => ({
        projectId: (row.project_id as string) ?? null,
        projektNummer: (row.project_number as string) ?? undefined,
        projektName: (row.project_name as string) ?? undefined,
        projektStatus: (row.projekt_status as string) ?? undefined,
        kundeId: (row.customer_id as string) ?? undefined,
        kundeName: (row.customer_name as string) ?? undefined,
        // bigint kommt aus PostgREST je nach Groesse als Zahl ODER als String.
        auftragswertCents: zahl(row.order_value_cents),
        auftragswertFixiertAm: (row.order_value_fixed_at as string) ?? undefined,
        fakturiertCents: zahl(row.fakturiert_netto_cents),
        fakturiert12mCents: zahl(row.fakturiert_12m_cents),
        offenCents: zahl(row.offen_cents),
        rechnungen: zahl(row.rechnungen),
        letzteRechnung: (row.letzte_rechnung as string) ?? undefined,
    }));
};

export const fetchNachkalkulation = async (
    von?: string | null,
    bis?: string | null,
): Promise<NachkalkulationRow[]> => {
    const { data, error } = await supabase.rpc('erp_nachkalkulation', {
        p_von: von || null,
        p_bis: bis || null,
    });
    if (error) throw new Error(describeError(error));
    return ((data as Record<string, unknown>[]) || []).map((row) => ({
        projectId: String(row.project_id ?? ''),
        projektNummer: (row.project_number as string) ?? undefined,
        projektName: (row.project_name as string) ?? undefined,
        kundeId: (row.customer_id as string) ?? undefined,
        kundeName: (row.customer_name as string) ?? undefined,
        erloeseCents: zahl(row.erloese_cents),
        freelancerKostenCents: zahl(row.freelancer_kosten_cents),
        begehungen: zahl(row.begehungen),
        begehungenOhneSatz: zahl(row.begehungen_ohne_satz),
        eigenleistungCents: zahl(row.eigenleistung_kalk_cents),
        deckungsbeitragCents: zahl(row.deckungsbeitrag_cents),
    }));
};

export interface LiquiditaetsBelege {
    rechnungen: LiquiditaetsBeleg[];
    gutschriften: LiquiditaetsBeleg[];
}

/**
 * Offene Ein- und Auszahlungen. Die RPC liefert beide Seiten in EINEM
 * Ergebnis (eine `art`-Spalte) — zwei Aufrufe koennten unterschiedliche
 * Stichtage sehen. Hier werden sie nur aufgeteilt, weil `berechneLiquiditaet`
 * sie getrennt entgegennimmt (Eingaenge bekommen den Zahlungsmoral-Malus,
 * Auszahlungen nicht).
 */
export const fetchLiquiditaetsBelege = async (): Promise<LiquiditaetsBelege> => {
    const { data, error } = await supabase.rpc('erp_liquiditaet');
    if (error) throw new Error(describeError(error));

    const rechnungen: LiquiditaetsBeleg[] = [];
    const gutschriften: LiquiditaetsBeleg[] = [];
    for (const row of (data as Record<string, unknown>[]) || []) {
        const beleg: LiquiditaetsBeleg = {
            id: String(row.beleg_id ?? ''),
            art: row.art === 'gutschrift' ? 'gutschrift' : 'rechnung',
            nummer: (row.nummer as string) ?? undefined,
            partner: (row.partner as string) ?? undefined,
            faelligAm: (row.faellig_am as string) ?? undefined,
            betragCents: zahl(row.betrag_cents),
            zahlungsmoral: row.zahlungsmoral === null || row.zahlungsmoral === undefined
                ? undefined
                : zahl(row.zahlungsmoral),
        };
        if (beleg.art === 'gutschrift') gutschriften.push(beleg);
        else rechnungen.push(beleg);
    }
    return { rechnungen, gutschriften };
};

// ═══════════════════════════════════════════════════════════════════════════
// 2) Auftragswerte (project_order_values)
// ═══════════════════════════════════════════════════════════════════════════

export interface ProjectOrderValue {
    id: string;
    companyId: string;
    projectId: string;
    quoteId?: string;
    orderValueCents: number;
    fixedAt: string;
    fixedBy?: string;
    note?: string;
}

const mapOrderValue = (row: Record<string, unknown>): ProjectOrderValue => ({
    id: String(row.id),
    companyId: String(row.company_id ?? ''),
    projectId: String(row.project_id),
    quoteId: (row.quote_id as string) ?? undefined,
    orderValueCents: zahl(row.order_value_cents),
    fixedAt: String(row.fixed_at ?? ''),
    fixedBy: (row.fixed_by as string) ?? undefined,
    note: (row.note as string) ?? undefined,
});

export const fetchOrderValues = async (): Promise<ProjectOrderValue[]> => {
    const { data, error } = await supabase
        .from('project_order_values')
        .select('*')
        .order('fixed_at', { ascending: false });
    if (error) throw new Error(describeError(error));
    return ((data as Record<string, unknown>[]) || []).map(mapOrderValue);
};

/**
 * Der fixierte Auftragswert EINES Projekts, oder null. Wirft nicht: fehlt die
 * Migration Nr. 23 oder greift die RLS, gibt es eben keinen Vorschlag
 * (Teilbeauftragung: „bisher + dieser Teil").
 */
export const fetchOrderValue = async (projectId: string): Promise<ProjectOrderValue | null> => {
    try {
        const { data, error } = await supabase
            .from('project_order_values')
            .select('*')
            .eq('project_id', projectId)
            .maybeSingle();
        if (error || !data) return null;
        return mapOrderValue(data as Record<string, unknown>);
    } catch {
        return null;
    }
};

export interface OrderValueInput {
    projectId: string;
    orderValueCents: number;
    quoteId?: string;
    note?: string;
}

/**
 * Auftragswert setzen oder aendern.
 *
 * `upsert` auf `project_id`: die Tabelle traegt dort einen Unique-Index, es
 * gibt genau eine Zeile je Projekt. `fixed_at`/`fixed_by` ziehen bei jeder
 * Aenderung mit, damit in der Pflegeliste steht, wer wann zuletzt entschieden
 * hat.
 */
export const saveOrderValue = async (input: OrderValueInput): Promise<ProjectOrderValue> => {
    const companyId = await currentCompanyId();
    const { data: auth } = await supabase.auth.getUser();

    const { data, error } = await supabase
        .from('project_order_values')
        .upsert(
            {
                company_id: companyId,
                project_id: input.projectId,
                quote_id: input.quoteId ?? null,
                order_value_cents: Math.max(0, Math.round(input.orderValueCents)),
                fixed_at: new Date().toISOString(),
                fixed_by: auth?.user?.id ?? null,
                note: input.note?.trim() || null,
            },
            { onConflict: 'project_id' },
        )
        .select()
        .single();
    if (error) throw new Error(describeError(error));
    return mapOrderValue(data as Record<string, unknown>);
};

export const deleteOrderValue = async (projectId: string): Promise<void> => {
    const { error } = await supabase
        .from('project_order_values')
        .delete()
        .eq('project_id', projectId);
    if (error) throw new Error(describeError(error));
};

/**
 * Auftragswert bei der Angebotsannahme fixieren — BEST EFFORT.
 *
 * Der Aufrufer ist `acceptQuote` (crmService.ts), also der Pfad, an dessen
 * Ende ein Projekt steht. Ein Fehler hier (fehlende Migration Nr. 23,
 * fehlende company_id, RLS) darf die bereits angelegte Projektzeile NICHT
 * kippen — der Wert laesst sich in der Pflegeliste des Cockpits jederzeit
 * nachtragen. Deshalb wird geloggt und `false` zurueckgegeben, nicht geworfen.
 */
export const fixiereAuftragswertBestEffort = async (
    input: OrderValueInput,
): Promise<boolean> => {
    try {
        await saveOrderValue(input);
        return true;
    } catch (e) {
        console.warn('Auftragswert konnte nicht fixiert werden (nicht-fatal):', e);
        return false;
    }
};

// ═══════════════════════════════════════════════════════════════════════════
// 3) Eigenleistungssaetze (effort_rates)
// ═══════════════════════════════════════════════════════════════════════════

export interface EffortRate {
    id: string;
    companyId: string;
    coordinatorName: string;
    userId?: string;
    satzJeBegehungCents: number;
    satzJePlanungsstundeCents?: number;
    gueltigAb: string;
    note?: string;
}

const mapEffortRate = (row: Record<string, unknown>): EffortRate => ({
    id: String(row.id),
    companyId: String(row.company_id ?? ''),
    coordinatorName: String(row.coordinator_name ?? ''),
    userId: (row.user_id as string) ?? undefined,
    satzJeBegehungCents: zahl(row.satz_je_begehung_cents),
    satzJePlanungsstundeCents:
        row.satz_je_planungsstunde_cents === null || row.satz_je_planungsstunde_cents === undefined
            ? undefined
            : zahl(row.satz_je_planungsstunde_cents),
    gueltigAb: String(row.gueltig_ab ?? ''),
    note: (row.note as string) ?? undefined,
});

export const fetchEffortRates = async (): Promise<EffortRate[]> => {
    const { data, error } = await supabase
        .from('effort_rates')
        .select('*')
        .order('coordinator_name', { ascending: true })
        .order('gueltig_ab', { ascending: false });
    if (error) throw new Error(describeError(error));
    return ((data as Record<string, unknown>[]) || []).map(mapEffortRate);
};

export interface EffortRateInput {
    id?: string;
    coordinatorName: string;
    userId?: string;
    satzJeBegehungCents: number;
    satzJePlanungsstundeCents?: number | null;
    gueltigAb: string;
    note?: string;
}

export const saveEffortRate = async (input: EffortRateInput): Promise<EffortRate> => {
    const companyId = await currentCompanyId();
    const row = {
        company_id: companyId,
        coordinator_name: input.coordinatorName.trim(),
        user_id: input.userId ?? null,
        satz_je_begehung_cents: Math.max(0, Math.round(input.satzJeBegehungCents)),
        satz_je_planungsstunde_cents:
            input.satzJePlanungsstundeCents === null || input.satzJePlanungsstundeCents === undefined
                ? null
                : Math.max(0, Math.round(input.satzJePlanungsstundeCents)),
        gueltig_ab: input.gueltigAb,
        note: input.note?.trim() || null,
    };

    const query = input.id
        ? supabase.from('effort_rates').update(row).eq('id', input.id)
        : supabase.from('effort_rates').insert(row);

    const { data, error } = await query.select().single();
    if (error) throw new Error(describeError(error));
    return mapEffortRate(data as Record<string, unknown>);
};

export const deleteEffortRate = async (id: string): Promise<void> => {
    const { error } = await supabase.from('effort_rates').delete().eq('id', id);
    if (error) throw new Error(describeError(error));
};

// ═══════════════════════════════════════════════════════════════════════════
// 4) Kapazitaetsbasis
// ═══════════════════════════════════════════════════════════════════════════

export interface KapazitaetsBasis {
    projekte: (KapazitaetProjekt & { zip?: string })[];
    assignments: KapazitaetAssignment[];
    koordinatoren: KapazitaetKoordinator[];
    freelancer: KapazitaetFreelancer[];
    /** Was NICHT geladen werden konnte — die Ansicht sagt es, statt zu schweigen. */
    warnungen: string[];
}

/**
 * Status, in denen ein Projekt keine Begehungen mehr erzeugt
 * (src/types.ts::ProjectStatus). 'on_hold' bleibt DRIN: es ruht nur, und
 * `onHoldUntil` sagt bis wann — das rechnet berechneKapazitaet aus.
 */
const ABGESCHLOSSENE_PROJEKT_STATUS = new Set(['completed', 'finished']);

/**
 * Alles, was die Kapazitaetsampel braucht — in einem Rutsch und einzeln
 * fehlertolerant (Muster useCrmMap): fehlt das Freelancer-Modul, faellt nur
 * die gelbe Stufe der Ampel weg, nicht die ganze Karte.
 *
 * Das Begehungs-SOLL kommt aus `data.metadata.intervalConfig` — dem Feld, aus
 * dem die App auch den naechsten Termin rechnet. Begruendung und die
 * Umrechnung Turnus → Begehungen/Monat stehen in
 * erpSteering.ts::begehungenProMonat.
 */
export const fetchKapazitaetsBasis = async (): Promise<KapazitaetsBasis> => {
    const warnungen: string[] = [];

    // Projekte und Einsatzauftraege seitenweise: beide wachsen ungefiltert auf
    // die stille 1000er-Grenze von PostgREST zu (Vorfall 23.10.2026).
    const [projektRows, assignmentRows, profilRows, freelancerRows, qualiRows] = await Promise.all([
        ladeAlleSeiten<Record<string, unknown>>((von, bis) =>
            supabase
                .from('projects')
                .select('id, name, status, start_date, end_date, coordinator_name, metadata:data->metadata')
                .order('id')
                .range(von, bis))
            .catch((e) => {
                warnungen.push(`Projekte: ${describeError(e)}`);
                return [] as Record<string, unknown>[];
            }),
        ladeAlleSeiten<Record<string, unknown>>((von, bis) =>
            supabase
                .from('inspection_assignments')
                // `id` mitselektiert: daran verwirft ladeAlleSeiten Dubletten
                // aus Anlagen zwischen zwei Seiten (sonst doppelte Auslastung).
                .select('id, project_id, zeitraum_von, zeitraum_bis, status')
                .order('id')
                .range(von, bis))
            .catch(() => [] as Record<string, unknown>[]),
        supabase
            .from('profiles')
            .select('id, first_name, last_name, role')
            .then((r) => {
                if (r.error) throw r.error;
                return (r.data as Record<string, unknown>[]) || [];
            })
            .catch((e) => {
                warnungen.push(`Profile: ${describeError(e)}`);
                return [] as Record<string, unknown>[];
            }),
        supabase
            .from('freelancers')
            .select('id, name, zip, status')
            .then((r) => (r.error ? [] : ((r.data as Record<string, unknown>[]) || [])))
            .catch(() => [] as Record<string, unknown>[]),
        supabase
            .from('freelancer_qualifications')
            .select('freelancer_id, typ, gueltig_bis')
            .then((r) => (r.error ? [] : ((r.data as Record<string, unknown>[]) || [])))
            .catch(() => [] as Record<string, unknown>[]),
    ]);

    const projekte = projektRows
        .filter((row) => !ABGESCHLOSSENE_PROJEKT_STATUS.has(String(row.status ?? '').toLowerCase()))
        .map((row) => {
            const meta = (row.metadata as Report['metadata'] | null) || undefined;
            const adresse = (meta as { projectAddress?: string } | undefined)?.projectAddress;
            return {
                id: String(row.id),
                name: (row.name as string) ?? undefined,
                intervalConfig: meta?.intervalConfig ?? null,
                // Spaltenwert zuerst (er ist indiziert und wird beim Speichern
                // gepflegt), Metadaten als Rueckfall fuer Altbestand.
                startDate: (row.start_date as string) || meta?.globalData?.startDate || undefined,
                endDate: (row.end_date as string) || meta?.globalData?.endDate || undefined,
                onHoldUntil: meta?.globalData?.onHoldUntil || undefined,
                zip: plzAusText(adresse) || undefined,
            };
        });

    const assignments: KapazitaetAssignment[] = assignmentRows.map((row) => ({
        projectId: String(row.project_id),
        zeitraumVon: String(row.zeitraum_von ?? ''),
        zeitraumBis: String(row.zeitraum_bis ?? ''),
        status: String(row.status ?? ''),
    }));

    // Koordinator = jedes interne Profil. Es gibt keine Rolle 'coordinator'
    // (src/types.ts::UserProfile) — wer koordiniert, entscheidet sich je
    // Projekt. Ausgeschlossen ist nur die externe Rolle.
    const koordinatoren: KapazitaetKoordinator[] = profilRows
        .filter((row) => String(row.role ?? '') !== 'freelancer')
        .map((row) => ({
            id: String(row.id),
            name: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || undefined,
            begehungenProMonat: KAPAZITAET_KOORDINATOR_DEFAULT,
        }));

    const qualisJeFreelancer = new Map<string, FreelancerQualification[]>();
    for (const q of qualiRows) {
        const key = String(q.freelancer_id);
        const eintrag: FreelancerQualification = {
            id: '',
            freelancerId: key,
            typ: q.typ as QualiTyp,
            gueltigBis: (q.gueltig_bis as string) ?? undefined,
        };
        const liste = qualisJeFreelancer.get(key);
        if (liste) liste.push(eintrag);
        else qualisJeFreelancer.set(key, [eintrag]);
    }

    const heute = new Date().toISOString().slice(0, 10);
    const freelancer: KapazitaetFreelancer[] = freelancerRows.map((row) => {
        const id = String(row.id);
        // Dieselbe Pruefung, die auch `grant_project_access` serverseitig
        // fahrt: Status aktiv + gueltige Pflichtnachweise. Ein Freelancer mit
        // abgelaufenem Nachweis ist keine Kapazitaet.
        const bereit = einsatzbereitschaft(
            { status: row.status as FreelancerStatus },
            qualisJeFreelancer.get(id) || [],
            heute,
        ).bereit;
        return {
            id,
            name: (row.name as string) ?? undefined,
            begehungenProMonat: KAPAZITAET_FREELANCER_DEFAULT,
            einsatzbereit: bereit,
        };
    });

    return { projekte, assignments, koordinatoren, freelancer, warnungen };
};

// ═══════════════════════════════════════════════════════════════════════════
// 5) Kundenstamm fuer die Nachkalkulation
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Nur die vier Felder, die die Nachkalkulation zur Gruppierung braucht.
 * Bewusst NICHT `fetchCustomers()` aus crmService: das laedt den vollen
 * Kundensatz inklusive Kontakten, Rahmenvertraegen und Aenderungshistorie —
 * fuer eine Branchen- und Regionsverdichtung ist das ein Vielfaches der
 * noetigen Datenmenge.
 *
 * Seitenweise: customers hat 1244 Zeilen (PROD 23.10.2026). Ohne `.range()`
 * kappte PostgREST still bei 1000 — und mangels Sortierung an unbestimmter
 * Stelle.
 */
export const fetchKundenStamm = async (): Promise<Pick<Customer, 'id' | 'name' | 'branche' | 'zip'>[]> => {
    const rows = await ladeAlleSeiten<Record<string, unknown>>((von, bis) =>
        supabase.from('customers').select('id, name, branche, zip').order('id').range(von, bis))
        .catch((error: unknown) => {
            throw new Error(describeError(error));
        });
    return rows.map((row) => ({
        id: String(row.id),
        name: String(row.name ?? ''),
        branche: (row.branche as string) ?? undefined,
        zip: (row.zip as string) ?? undefined,
    }));
};
